import moment from "moment";
import { VALUE_ROW_NAMES } from "./value_rows";
import { getValueRowNames, getSystemKeyByName } from "../../utils/row_name";

/**
 * @typedef {Object} ValueEntry
 * @property {number} item_id 土壌診断項目のID
 * @property {number[]} values 測定値の配列
 * @property {number|null} computed 計算された値（数値または null）
 */
/**
 * @typedef {Object} GroupedRow
 * @property {number} receiptId 受付番号
 * @property {number} receiptSubId 受付副番号（枝番号）
 * @property {string} userName ユーザ名
 * @property {string} date 採土日（YYYY-MM-DD形式、または"Invalid date"）
 * @property {ValueEntry[]} data 測定値の配列
 */

/**
 * @typedef {Object} SoilDiagItem
 * @property {number} id
 * @property {boolean} is_system
 * @property {string} system_key
 * @property {boolean} is_computed
 * @property {boolean} is_default
 * @property {string} name
 * @property {string} unit
 */

/**
 * @typedef {Record<string, number>} SystemKeyToItemIdMap
 * システムキーを土壌診断項目IDにマッピングするオブジェクト
 * 例: { "ph": 1, "ec": 2, ... }
 */

/**
 * CSVの行を解析して、受付番号と受付副番号ごとにグルーピングする
 * @param {string[][]} body CSVの行の要素配列（ヘッダー行を除く）
 * @param {string[]} header CSVのヘッダー行の要素（=項目）の配列
 * @param {SoilDiagItem[]} soilDiagItems 土壌診断項目の配列（/v2/soil_diagnosis_items のレスポンス）
 * @returns {GroupedRow[]} グルーピングされたCSVデータの配列
 */
export function parseBody(body, header, soilDiagItems) {
  const valueNames = getValueRowNames(VALUE_ROW_NAMES);

  /** @type {SystemKeyToItemIdMap} */
  const systemKeyToItemId = soilDiagItems.reduce((acc, item) => {
    acc[item.system_key] = item.id;
    return acc;
  }, {});

  /** @type {Record<string, GroupedRow>} */
  const grouped = {};

  body.forEach((rowArray) => {
    /** @type {Record<string, string>} CSV の行をヘッダーと対応させたオブジェクト 例：{'ユーザ№': '1', '圃場№': '1', ...} */
    const row = Object.fromEntries(header.map((h, i) => [h, rowArray[i]]));

    // 測定値以外は無視する
    if (row["種類"] !== "測定値") {
      return;
    }

    const receiptId = parseInt(row["受付番号"], 10);
    const receiptSubId = parseInt(row["受付副番号"], 10);

    const formattedDate = moment(
      row["採土日"],
      ["YYYY/M/D"],
      true,
    ).format("YYYY-MM-DD");

    // 受付番号と受付副番号の組み合わせでグルーピング
    const key = `${receiptId}_${receiptSubId}`;

    if (!grouped[key]) {
      grouped[key] = {
        receiptId,
        receiptSubId,
        userName: row["ユーザ名"] || "",
        date: formattedDate === "Invalid date" ? "Invalid date" : formattedDate,
        data: [],
      };
    }

    valueNames.forEach((name) => {
      const raw = row[name];
      if (raw === undefined || raw === "") {
        return;
      }

      const numeric = Number(raw);
      if (Number.isNaN(numeric)) {
        return;
      }

      const systemKey = getSystemKeyByName(name, VALUE_ROW_NAMES);
      const itemId = systemKeyToItemId[systemKey];

      if (!itemId) {
        return;
      }

      grouped[key].data.push({
        item_id: itemId,
        values: [numeric],
        computed: numeric,
      });
    });
  });
  return Object.values(grouped);
}

/**
 * グループ化された CSV 行から、数値として有効な測定値を抽出する
 * @param {GroupedRow} parsed parseBody 関数で生成されたグループ化された CSV 行のオブジェクト
 * @returns {ValueEntry[]} 数値として有効な測定値の配列
 */
export function getValues(parsed) {
  const isValid = parsed.data.every((d) => !Number.isNaN(d.computed));
  if (!isValid) {
    throw Error("Invalid value in CSV row");
  }
  return parsed.data
    .filter((d) => d.computed !== null)
    .map((d) => ({
      item_id: d.item_id,
      values: d.values,
      computed: d.computed,
    }));
}
